import React, { useEffect, useState, useCallback } from 'react';
import { api as axios } from '../utils/apiClient';

const PluginLogs = ({ site }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLogs = React.useCallback(async () => {
    if (!site?._id) {
      console.log('[PluginLogs] No site ID available');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log('[PluginLogs] Fetching logs for site:', site._id);
      const response = await axios.get(`/api/sites/${site._id}/plugin-logs?limit=200`);
      console.log('[PluginLogs] API Response:', {
        success: response.data?.success,
        count: response.data?.count,
        logsLength: response.data?.logs?.length,
        firstLog: response.data?.logs?.[0]
      });
      
      if (response.data?.success) {
        const allLogs = response.data.logs || [];
        console.log('[PluginLogs] Total logs received:', allLogs.length);
        console.log('[PluginLogs] All log actions:', allLogs.map(log => ({ action: log.action, plugin: log.pluginName, createdAt: log.createdAt })));
        
        // Filter logs: Only show user-initiated actions (activated, deactivated, update_*, enabled_auto_update, disabled_auto_update)
        // Exclude "installed" logs unless they have a reason (user-provided)
        const validActions = ['activated', 'deactivated', 'update_success', 'update_failed', 'update_attempt', 'enabled_auto_update', 'disabled_auto_update'];
        let filteredLogs = allLogs.filter(log => {
          const action = log.action?.toLowerCase() || '';
          // Include valid user actions
          if (validActions.includes(action)) {
            return true;
          }
          // Include "installed" only if it has a user-provided reason (not system-generated)
          if (action === 'installed' && log.reason && log.reason.trim() !== '') {
            return true;
          }
          return false;
        });
        
        // Sort logs: prioritize logs with reasons (user actions), then by date (newest first)
        filteredLogs.sort((a, b) => {
          // First priority: logs with user-provided reasons
          const aHasReason = !!(a.reason && a.reason.trim() !== '');
          const bHasReason = !!(b.reason && b.reason.trim() !== '');
          if (aHasReason && !bHasReason) return -1;
          if (!aHasReason && bHasReason) return 1;
          
          // Second priority: user actions over system actions
          const actionPriority = {
            'activated': 1,
            'deactivated': 1,
            'update_success': 1,
            'update_failed': 1,
            'update_attempt': 1,
            'enabled_auto_update': 1,
            'disabled_auto_update': 1,
            'installed': 2
          };
          const priorityA = actionPriority[a.action?.toLowerCase()] || 3;
          const priorityB = actionPriority[b.action?.toLowerCase()] || 3;
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          // Third priority: newest first
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        
        console.log('[PluginLogs] Filtered logs count:', filteredLogs.length);
        console.log('[PluginLogs] Valid actions we show:', validActions);
        
        // Debug: Log all actions found in filtered logs
        const actionBreakdown = {};
        filteredLogs.forEach(log => {
          const action = log.action?.toLowerCase() || 'unknown';
          actionBreakdown[action] = (actionBreakdown[action] || 0) + 1;
        });
        console.log('[PluginLogs] Filtered logs action breakdown:', actionBreakdown);
        
        // Debug: Specifically check for auto-update enable/disable logs
        const autoUpdateLogs = filteredLogs.filter(log => {
          const action = log.action?.toLowerCase() || '';
          return action === 'enabled_auto_update' || action === 'disabled_auto_update';
        });
        console.log('[PluginLogs] Found auto-update enable/disable logs:', autoUpdateLogs.length);
        if (autoUpdateLogs.length > 0) {
          console.log('[PluginLogs] Auto-update logs:', autoUpdateLogs.map(log => ({
            action: log.action,
            pluginName: log.pluginName,
            createdAt: log.createdAt
          })));
        }
        
        setLogs(filteredLogs);
      } else {
        const errorMsg = response.data?.message || 'Failed to load logs';
        console.error('[PluginLogs] API returned error:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('[PluginLogs] Failed to load plugin logs:', err);
      console.error('[PluginLogs] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError(err.response?.data?.message || err.message || 'Failed to load plugin logs');
    } finally {
      setLoading(false);
    }
  }, [site?._id]);

  useEffect(() => {
    fetchLogs();
    
    // Listen for auto-update enable/disable events to refresh logs
    const handleAutoUpdateChange = () => {
      console.log('[PluginLogs] Auto-update changed, refreshing logs...');
      fetchLogs();
    };
    
    window.addEventListener('auto-update-changed', handleAutoUpdateChange);
    window.addEventListener('plugin-updated', handleAutoUpdateChange);
    
    return () => {
      window.removeEventListener('auto-update-changed', handleAutoUpdateChange);
      window.removeEventListener('plugin-updated', handleAutoUpdateChange);
    };
  }, [fetchLogs]);

  const handleRefresh = async () => {
    if (!site?._id) return;
    setLoading(true);
    setError(null);
    try {
      console.log('[PluginLogs] Manually refreshing logs for site:', site._id);
      const response = await axios.get(`/api/sites/${site._id}/plugin-logs?limit=200`);
      if (response.data?.success) {
        const allLogs = response.data.logs || [];
        console.log('[PluginLogs] Refresh - Total logs:', allLogs.length);
        console.log('[PluginLogs] Refresh - All actions:', allLogs.map(log => log.action));
        // Filter logs: Only show user-initiated actions (activated, deactivated, update_*, enabled_auto_update, disabled_auto_update)
        // Exclude "installed" logs unless they have a reason (user-provided)
        const validActions = ['activated', 'deactivated', 'update_success', 'update_failed', 'update_attempt', 'enabled_auto_update', 'disabled_auto_update'];
        let filteredLogs = allLogs.filter(log => {
          const action = log.action?.toLowerCase() || '';
          // Include valid user actions
          if (validActions.includes(action)) {
            return true;
          }
          // Include "installed" only if it has a user-provided reason (not system-generated)
          if (action === 'installed' && log.reason && log.reason.trim() !== '') {
            return true;
          }
          return false;
        });
        
        // Sort logs: prioritize logs with reasons (user actions), then by date (newest first)
        filteredLogs.sort((a, b) => {
          // First priority: logs with user-provided reasons
          const aHasReason = !!(a.reason && a.reason.trim() !== '');
          const bHasReason = !!(b.reason && b.reason.trim() !== '');
          if (aHasReason && !bHasReason) return -1;
          if (!aHasReason && bHasReason) return 1;
          
          // Second priority: user actions over system actions
          const actionPriority = {
            'activated': 1,
            'deactivated': 1,
            'update_success': 1,
            'update_failed': 1,
            'update_attempt': 1,
            'enabled_auto_update': 1,
            'disabled_auto_update': 1,
            'installed': 2
          };
          const priorityA = actionPriority[a.action?.toLowerCase()] || 3;
          const priorityB = actionPriority[b.action?.toLowerCase()] || 3;
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          // Third priority: newest first
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        
        // Debug: Log all actions found in filtered logs
        const actionBreakdown = {};
        filteredLogs.forEach(log => {
          const action = log.action?.toLowerCase() || 'unknown';
          actionBreakdown[action] = (actionBreakdown[action] || 0) + 1;
        });
        console.log('[PluginLogs] Refresh - Filtered logs action breakdown:', actionBreakdown);
        
        // Debug: Specifically check for auto-update enable/disable logs
        const autoUpdateLogs = filteredLogs.filter(log => {
          const action = log.action?.toLowerCase() || '';
          return action === 'enabled_auto_update' || action === 'disabled_auto_update';
        });
        console.log('[PluginLogs] Refresh - Found auto-update enable/disable logs:', autoUpdateLogs.length);
        if (autoUpdateLogs.length > 0) {
          console.log('[PluginLogs] Refresh - Auto-update logs:', autoUpdateLogs.map(log => ({
            action: log.action,
            pluginName: log.pluginName,
            createdAt: log.createdAt
          })));
        }
        
        setLogs(filteredLogs);
        console.log('[PluginLogs] Refresh complete. Found', filteredLogs.length, 'filtered logs out of', allLogs.length, 'total logs');
      } else {
        setError(response.data?.message || 'Failed to load logs');
      }
    } catch (err) {
      console.error('[PluginLogs] Refresh failed:', err);
      setError(err.response?.data?.message || err.message || 'Failed to refresh logs');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="tab-content">Loading plugin logs…</div>;
  }

  if (error) {
    return (
      <div className="tab-content">
        <p className="error-message">Failed to load plugin logs: {error}</p>
      </div>
    );
  }

  if (!logs.length) {
    return (
      <div className="tab-content plugin-logs-tab">
        <div className="plugin-logs-header">
          <div className="plugin-logs-header-content">
            <p className="plugin-logs-description">
              Showing plugin activity logs: activate, deactivate, and update actions performed on this site.
            </p>
          </div>
          <div className="plugin-logs-actions">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="plugin-logs-refresh-btn"
            >
              {loading ? 'Refreshing...' : '↻ Refresh'}
            </button>
          </div>
        </div>
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          <p>No plugin log entries found for this site yet.</p>
          <p style={{ fontSize: '0.85em', marginTop: '10px', color: '#999' }}>
            Logs will appear here after you activate, deactivate, or update plugins using the reason popup.
          </p>
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '4px', textAlign: 'left', maxWidth: '600px', margin: '20px auto', border: '1px solid #ffc107' }}>
            <p style={{ fontWeight: 600, marginBottom: '10px', color: '#856404' }}>⚠️ Important: Why no logs are showing</p>
            <p style={{ fontSize: '0.9em', color: '#856404', marginBottom: '10px' }}>
              The system found logs in the database, but they all have action type <code>'installed'</code>, 
              which are monitoring events, not user actions with reasons.
            </p>
            <p style={{ fontWeight: 600, marginBottom: '10px', color: '#856404' }}>To create log entries that will appear here:</p>
            <ol style={{ marginLeft: '20px', color: '#856404', fontSize: '0.9em' }}>
              <li style={{ marginBottom: '8px' }}>Go to the <strong>Plugins</strong> tab (not WordPress admin)</li>
              <li style={{ marginBottom: '8px' }}>Click <strong>Activate</strong>, <strong>Deactivate</strong>, or <strong>Update</strong> on any plugin</li>
              <li style={{ marginBottom: '8px' }}>Enter a reason in the popup (e.g., "I don't need that plugin for now")</li>
              <li style={{ marginBottom: '8px' }}>Click the action button to proceed</li>
              <li>Return to this tab and click <strong>Refresh</strong> to see the log entry</li>
            </ol>
            <p style={{ fontSize: '0.85em', color: '#856404', marginTop: '10px', fontStyle: 'italic' }}>
              Note: Actions performed directly in WordPress admin won't create logs here. 
              You must use the buttons in the <strong>Plugins</strong> tab of this WebMonitor interface.
            </p>
          </div>
          {error && (
            <p style={{ fontSize: '0.85em', marginTop: '10px', color: '#d63638' }}>
              Error: {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content plugin-logs-tab">
      <div className="plugin-logs-header">
        <div className="plugin-logs-header-content">
          <p className="plugin-logs-description">
            Showing plugin activity logs: activate, deactivate, and update actions performed on this site.
          </p>
        </div>
        <div className="plugin-logs-actions">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={loading}
            className="plugin-logs-refresh-btn"
          >
            {loading ? 'Refreshing...' : '↻ Refresh'}
          </button>
        </div>
      </div>
      <div className="plugin-logs-table-wrapper">
        <table className="plugins-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ padding: '8px', textAlign: 'left' }}>Date & Time</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Plugin</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Action</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Roles</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '8px', textAlign: 'left' }}>Reason</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => {
              const date = log.createdAt ? new Date(log.createdAt) : null;
              
              // Map action to display format: activate, deactivate, update, or enabled auto update
              let action = 'update'; // default
              const logAction = log.action?.toLowerCase() || '';
              
              // Check if this is an auto-update action
              // Auto-update can be identified by:
              // 1. System email/role (from backend auto-update service)
              // 2. Reason containing auto-update keywords (from frontend immediate update)
              // 3. Message containing auto-update keywords
              const reasonLower = log.reason ? log.reason.toLowerCase() : '';
              const messageLower = log.message ? log.message.toLowerCase() : '';
              const isAutoUpdate = log.email === 'auto-update@system' || 
                                 log.role === 'system' ||
                                 reasonLower.includes('(auto-update)') ||
                                 reasonLower.includes('auto-update enabled') ||
                                 reasonLower.includes('auto-update') ||
                                 reasonLower.includes('automatic auto-update') ||
                                 reasonLower.includes('auto update enabled') ||
                                 reasonLower.includes('auto update') ||
                                 messageLower.includes('auto-updated') ||
                                 messageLower.includes('auto-update');
              
              if (logAction === 'activated') {
                action = 'activate';
              } else if (logAction === 'deactivated') {
                action = 'deactivate';
              } else if (logAction === 'enabled_auto_update') {
                action = 'Enabled Auto Update';
              } else if (logAction === 'disabled_auto_update') {
                action = 'Disabled Auto Update';
              } else if (logAction.includes('update')) {
                // Distinguish between auto-update and manual update
                if (isAutoUpdate) {
                  action = 'auto update';
                } else {
                  action = 'update';
                }
              } else if (logAction === 'installed') {
                action = 'update'; // Show installed as update for display purposes
              }
              
              // Format role: only allow admin, manager, developer, viewer
              let role = log.role?.toLowerCase() || 'N/A';
              const validRoles = ['admin', 'manager', 'developer', 'viewer'];
              if (!validRoles.includes(role) && role !== 'N/A') {
                // Try to map common role variations
                if (role.includes('admin') || role.includes('administrator')) {
                  role = 'admin';
                } else if (role.includes('manager')) {
                  role = 'manager';
                } else if (role.includes('developer') || role.includes('dev')) {
                  role = 'developer';
                } else if (role.includes('viewer') || role.includes('view')) {
                  role = 'viewer';
                } else {
                  role = 'N/A';
                }
              }
              
              // Format reason - prioritize user-provided reason
              let reason = 'N/A';
              if (log.reason) {
                reason = log.reason;
              } else if (log.message) {
                reason = log.message;
              } else if (log.errorCode) {
                reason = log.errorCode;
              }
              
              return (
                <tr key={log._id} style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '8px', fontSize: '0.9em' }}>
                    {date ? date.toLocaleString() : 'N/A'}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.9em' }}>
                    <strong>{log.pluginName || log.pluginSlug || 'Unknown plugin'}</strong>
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.9em' }}>
                    {action}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.9em', textTransform: 'capitalize' }}>
                    {role}
                  </td>
                  <td style={{ padding: '8px', fontSize: '0.9em' }}>{log.email || 'N/A'}</td>
                  <td style={{ padding: '8px', fontSize: '0.9em', maxWidth: '260px' }}>
                    {reason}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PluginLogs;


